import {
  AVATAR_SIZE, NO_INSETS, WIDGET_SAFE_MARGIN, avoidReservedZones,
  type Insets, type Point, type Rect, type Size,
} from './widgetPosition';

/**
 * CORE-1120 — что именно лежит под иконкой прямо сейчас.
 *
 * Реестр занятых областей закрыл ФИКСИРОВАННЫЕ слои: шапка объявляет себя, и
 * иконка её обходит. QA на деве (390×844, `/masters`) показала вторую половину:
 * launcher в своём углу по умолчанию сел на «Все мастера» и «Сначала новые» —
 * обычные элементы страницы, которые никто не объявлял и объявить не может.
 * Они прокручиваются, у каждой страницы свои, и просить каждую регистрировать
 * свои кнопки значит не решить задачу, а раздать её по экранам.
 *
 * Поэтому здесь — то, о чём прямо просит тикет: «overlap проверять не только
 * визуально, но и hit-testing». Спрашиваем у браузера, что под кружком, и
 * найденные интерактивные элементы кладём в тот же реестр. Дальше работает уже
 * известная геометрия — иконка обходит их так же, как шапку.
 *
 * Здесь только чистая часть: сами замеры живут в {@link ./useInteractiveOverlap}.
 */

/**
 * Что считается «активным элементом».
 *
 * Список широкий намеренно: react-native-web рисует `Pressable` то `div` с
 * `role="button"`, то фокусируемым `div` без роли — узкий список пропустил бы
 * ровно те кнопки, на которые жалуется QA. Ложное срабатывание стоит дёшево
 * (иконка встанет на пару десятков пикселей выше), пропуск — дорого.
 */
export const INTERACTIVE_SELECTOR = [
  'a[href]',
  'button',
  'input',
  'select',
  'textarea',
  '[role="button"]',
  '[role="link"]',
  '[role="tab"]',
  '[role="switch"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[role="menuitem"]',
  '[role="combobox"]',
  '[tabindex]',
].join(',');

/**
 * Доля экрана, после которой найденное перестаёт быть «элементом».
 *
 * Прокручиваемый контейнер тоже фокусируем и тоже попадёт в выдачу, накрыв
 * собой пол-экрана. Объявить такое занятой зоной — значит не оставить иконке ни
 * одного свободного места и получить ту же картинку, только после лишней
 * работы. Полполосы шириной во весь экран (кнопка на телефоне) — проходит;
 * половина экрана — нет.
 */
export const MAX_ZONE_AREA_RATIO = 0.4;

/**
 * Столько после прокрутки мутации страницы не считаются поводом перемерить.
 *
 * CORE-1120 однажды уже слушал прокрутку и двигал иконку на каждой её паузе.
 * Владелец на мобильном вебе: «двигается вверх-вниз при скролле» — под иконкой
 * непрерывно проезжают кнопки карточек, зона то появляется, то исчезает.
 * Слушатель убрали, но замер по ИЗМЕНЕНИЯМ СТРАНИЦЫ (см. ниже) вернул бы ту же
 * картинку: прокрутка длинного списка мутирует DOM не меньше, чем загрузка.
 *
 * Поэтому прокрутка здесь не запускает замер и не отменяет его — она отмечает
 * время. Мутации, пришедшие сразу за прокруткой, пропускаются: это контент
 * едет под иконкой, а он всё равно уедет дальше.
 */
export const SCROLL_QUIET_MS = 400;

/**
 * Стоит ли перемеривать после изменения страницы.
 *
 * Два правила, и оба — про то, чего делать НЕ надо:
 *
 *  1. Изменения внутри самого виджета игнорируются. Замер кладёт зону, зона
 *     двигает иконку, иконка меняет свой DOM — и без этого правила замер
 *     запускал бы сам себя по кругу.
 *  2. Изменения сразу за прокруткой игнорируются — см. {@link SCROLL_QUIET_MS}.
 */
export function shouldRemeasureAfterMutation(input: {
  insideWidgetLayer: boolean;
  msSinceScroll: number;
}): boolean {
  if (input.insideWidgetLayer) return false;
  return input.msSinceScroll >= SCROLL_QUIET_MS;
}

/** Точки, которыми щупаем кружок: центр и четыре точки ближе к краям. */
export function probePoints(anchor: Point, size: number = AVATAR_SIZE): Point[] {
  const half = size / 2;
  const off = size * 0.35;
  const cx = anchor.x + half;
  const cy = anchor.y + half;
  return [
    { x: cx, y: cy },
    { x: cx - off, y: cy - off },
    { x: cx + off, y: cy - off },
    { x: cx - off, y: cy + off },
    { x: cx + off, y: cy + off },
  ];
}

/**
 * Годится ли найденный прямоугольник в занятую зону.
 *
 * Пустые (скрытые элементы отдают 0×0) и слишком большие отсеиваются: и то и
 * другое сдвинуло бы иконку без пользы.
 */
export function isUsableZone(rect: Rect, viewport: Size): boolean {
  if (rect.width <= 0 || rect.height <= 0) return false;
  const viewportArea = viewport.width * viewport.height;
  if (viewportArea <= 0) return false;
  return (rect.width * rect.height) / viewportArea <= MAX_ZONE_AREA_RATIO;
}

/**
 * Сколько раз щупаем, прежде чем остановиться.
 *
 * Один круг — это то, с чем пришёл QA FAIL 04.09.2026 на `/masters`: иконка
 * честно ушла с фильтра «Все мастера» ровно на поле поиска, которое стоит
 * строкой выше и которого никто не щупал. Обход не отказал — ему дали неполный
 * вход. Поэтому кругов несколько: назначение тоже спрашивают, найденное там
 * кладут в тот же список и считают заново.
 *
 * Потолок нужен не от расхождения (список зон только растёт, а считается он от
 * БАЗОВОЙ точки, которая от результата не зависит), а от цены: каждый круг —
 * это пять `elementsFromPoint` и замеры прямоугольников. Четыре круга покрывают
 * страницу, где занятые элементы стоят друг над другом в три ряда; дальше
 * дешевле оставить иконку там, где она есть.
 */
export const MAX_PROBE_ROUNDS = 4;


/**
 * Куда бы иконка ни уехала — там тоже смотрим. Весь алгоритм замера, без DOM.
 *
 * `probe` отвечает на единственный вопрос, ради которого нужен браузер: «какие
 * активные элементы лежат под кружком в этой точке». Всё остальное — счёт, и он
 * живёт здесь, чтобы сценарии из отчётов QA проверялись прямыми числами, а не
 * чтением исходника.
 *
 * Порядок круга: посмотреть, что здесь → посчитать той же геометрией, куда
 * иконка после этого уедет → перейти туда. Останавливаемся, как только круг не
 * принёс ничего нового: под точкой никого нет, либо назначение свободно, либо
 * деваться некуда и геометрия вернула ту же точку — следующий круг нашёл бы
 * ровно то же самое.
 *
 * Считается всё от `base` и только от неё: список зон растёт, точка отсчёта не
 * двигается. Поэтому ответ — функция страницы и базовой точки, а не текущей
 * отрисованной позиции, и от кадра к кадру он не меняется.
 *
 * Отдаётся СПИСОК, а не общий bounding box, как было раньше. С одним кругом
 * объединение было почти безобидно, с несколькими стало бы вредно: поле поиска
 * вместе с фильтром под ним накрывают полосу во всю ширину экрана, ещё пара
 * кругов — и объединение перевалит за {@link MAX_ZONE_AREA_RATIO}, зона
 * снимется целиком, и иконка вернётся на тот самый элемент, с которого уходила.
 * Чем больше мы узнавали бы о странице, тем хуже был бы ответ. Списком порог
 * применяется к каждому элементу отдельно, а промежутки между ними остаются
 * свободными — там иконке и место.
 */
export function resolveBlockedZones(params: {
  base: Point;
  /** Чужие зоны — шапка, объявленные экраном области. Вход геометрии наравне. */
  declared?: readonly Rect[];
  viewport: Size;
  insets?: Insets;
  size?: number;
  margin?: number;
  probe: (anchor: Point) => readonly Rect[];
}): Rect[] {
  const {
    base, declared = [], viewport,
    insets = NO_INSETS, size = AVATAR_SIZE, margin = WIDGET_SAFE_MARGIN, probe,
  } = params;

  // Ключ — сама геометрия прямоугольника: один и тот же элемент попадается и
  // под базой, и под назначением, а зона у него одна.
  const found = new Map<string, Rect>();
  let at = base;

  for (let round = 0; round < MAX_PROBE_ROUNDS; round += 1) {
    const before = found.size;
    for (const rect of probe(at)) {
      if (!isUsableZone(rect, viewport)) continue;
      found.set(`${rect.x}:${rect.y}:${rect.width}:${rect.height}`, rect);
    }
    if (found.size === before) break;
    at = avoidReservedZones(
      base, [...declared, ...found.values()], viewport, insets, size, margin,
    );
  }

  return [...found.values()];
}
